import { Dispatch, FC, MutableRefObject, SetStateAction } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { deleteUtkast } from 'store/utkastSlice';

import { useAppDispatch } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog, RaaSpinner } from 'raa-react';

import PubliceraUtkastDialog from 'components/utkast/publiceraUtkastDialog/PubliceraUtkastDialog';

import { BebyggelseobjektUtkast } from 'models';
import { RequestStatus, RequestType } from 'models/appModels/Request';

import { AktivitetstypEnum } from 'enums';

import { setNestedObjectValues, useFormikContext } from 'formik';
import { AnyObject } from 'yup';

interface UtkastActionsSectionProps {
  utkastData: BebyggelseobjektUtkast;
  requestStatus: RequestStatus;
  requestType: RequestType;
  setFormValidationSchema: Dispatch<SetStateAction<AnyObject>>;
  sparaFormSchema: AnyObject;
  publiceraFormSchema: AnyObject;
  deletedOrPublishedRef: MutableRefObject<boolean>;
}

const UtkastActionsSection: FC<UtkastActionsSectionProps> = ({
  utkastData,
  requestStatus,
  requestType,
  setFormValidationSchema,
  sparaFormSchema,
  publiceraFormSchema,
  deletedOrPublishedRef
}) => {
  const { setDialog, unsetDialog } = useDialog();
  const { dirty, isValid, setTouched, validateForm, setSubmitting } = useFormikContext();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const proceedDeletingUtkast = async () => {
    if (utkastData && utkastData.utkast_id) {
      const aktivitetsRef = utkastData.aktivitetref;
      const deleteAction = await dispatch(deleteUtkast(utkastData.utkast_id));

      if (deleteAction.meta.requestStatus === 'fulfilled') {
        deletedOrPublishedRef.current = true;
        unsetDialog();
        navigate(`/aktivitet/${aktivitetsRef.aktivitet_id}`, { replace: true });
      }
    }
  };

  return (
    <div className="tw-flex tw-w-full tw-flex-wrap tw-gap-2">
      <button
        className="raa-button raa-button-cancel !tw-min-w-[7.25rem] tw-flex-1"
        type="button"
        disabled={requestStatus === 'pending'}
        hidden={
          ![AktivitetstypEnum.INVENTERING, AktivitetstypEnum.ENKELINVENTERING].includes(
            utkastData.aktivitetref.aktivitetstyp
          )
        }
        onClick={() => {
          setDialog(RaaDialog, {
            header: 'Vill du verkligen slänga utkastet?',
            proceed: proceedDeletingUtkast,
            cancel: unsetDialog,
            proceedButtonText: 'släng',
            close: unsetDialog,
            children: (
              <p id="dialog-description" className="tw-mt-8">
                Detta påverkar inte publicerade objekt.
              </p>
            ),
            ariaDescribedBy: 'dialog-description'
          });
        }}
      >
        släng
      </button>

      <button
        className="raa-button raa-button-confirm tw-flex tw-w-full !tw-min-w-[7.25rem] tw-flex-1 tw-flex-col tw-items-center tw-justify-center"
        type="submit"
        disabled={requestStatus === 'pending' || !dirty}
        onClick={() => setFormValidationSchema(sparaFormSchema)}
      >
        {requestStatus === 'pending' && requestType === 'put' ? <RaaSpinner size="1rem" /> : 'Spara'}
      </button>

      <button
        className="raa-button !tw-min-w-[7.25rem] tw-flex-1"
        type="button"
        disabled={requestStatus === 'pending' || dirty || !isValid}
        hidden={
          ![
            AktivitetstypEnum.INVENTERING,
            AktivitetstypEnum.ENKELINVENTERING,
            AktivitetstypEnum.KULTURVARDE_FORSTORT
          ].includes(utkastData.aktivitetref.aktivitetstyp)
        }
        onClick={async () => {
          setFormValidationSchema(publiceraFormSchema);
          setTimeout(async () => {
            const errors = await validateForm();
            if (Object.keys(errors).length === 0 && id) {
              setDialog(PubliceraUtkastDialog, { id, onSuccess: () => (deletedOrPublishedRef.current = true) });
            } else {
              await setTouched(setNestedObjectValues(errors, true));
              setSubmitting(true);
            }
          }, 0);
        }}
      >
        publicera
      </button>
    </div>
  );
};

export default UtkastActionsSection;
